// EditAgentDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Brain, Sparkles, Cpu, Settings, CheckCircle, RefreshCw } from 'lucide-react';
import { MODEL_OPTIONS, AI_PROVIDERS } from '../constants';

/**
 * Dialog for editing AI agent configuration
 * 
 * @param {Object} props
 * @param {Object|null} props.agent - The agent being edited (null = closed)
 * @param {function} props.onAgentChange - Callback when agent data changes
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 * @param {boolean} props.saving - Whether save is in progress
 */
export const EditAgentDialog = ({
  agent,
  onAgentChange,
  onSave,
  onCancel,
  saving = false
}) => {
  const handleConfigChange = (key, value) => {
    onAgentChange({
      ...agent,
      ai_config: { ...agent.ai_config, [key]: value }
    });
  };

  const handleProviderChange = (brain, provider) => {
    const modelKey = `${brain}_model`;
    const providerKey = `${brain}_provider`;
    onAgentChange({
      ...agent,
      ai_config: {
        ...agent.ai_config,
        [providerKey]: provider,
        [modelKey]: MODEL_OPTIONS[provider]?.[0]?.value || ''
      }
    });
  };

  return (
    <Dialog open={!!agent} onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Edit AI Agent: {agent?.name}
          </DialogTitle>
          <DialogDescription>
            Configure the agent&apos;s profile, capabilities, and AI model settings
          </DialogDescription>
        </DialogHeader>
        
        {agent && (
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Agent Name</Label>
                <Input
                  value={agent.name || ''}
                  onChange={(e) => onAgentChange({ ...agent, name: e.target.value })}
                  placeholder="e.g., Ava"
                  data-testid="edit-agent-name"
                />
              </div>
              <div>
                <Label>Role Title</Label>
                <Input
                  value={agent.role_title || ''}
                  onChange={(e) => onAgentChange({ ...agent, role_title: e.target.value })}
                  placeholder="e.g., Lead Qualification Coordinator"
                  data-testid="edit-agent-role"
                />
              </div>
            </div>

            <div>
              <Label>Mission Statement</Label>
              <Textarea
                value={agent.mission || ''}
                onChange={(e) => onAgentChange({ ...agent, mission: e.target.value })}
                placeholder="Describe what this agent is responsible for..."
                rows={3}
                data-testid="edit-agent-mission"
              />
            </div>

            {/* AI Configuration */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">AI Brain Configuration</Label>
              <p className="text-sm text-muted-foreground">
                Configure different AI models for each processing component
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Left Brain */}
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Brain className="h-4 w-4 text-blue-600" />
                      Left Brain
                    </CardTitle>
                    <CardDescription className="text-xs">Analytical & Logical</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <Label className="text-xs">Provider</Label>
                      <Select
                        value={agent.ai_config?.left_brain_provider || 'openai'}
                        onValueChange={(val) => handleProviderChange('left_brain', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-left-provider">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_PROVIDERS.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Model</Label>
                      <Select
                        value={agent.ai_config?.left_brain_model || 'gpt-4o'}
                        onValueChange={(val) => handleConfigChange('left_brain_model', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-left-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS[agent.ai_config?.left_brain_provider || 'openai']?.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Right Brain */}
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      Right Brain
                    </CardTitle>
                    <CardDescription className="text-xs">Creative & Intuitive</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <Label className="text-xs">Provider</Label>
                      <Select
                        value={agent.ai_config?.right_brain_provider || 'anthropic'}
                        onValueChange={(val) => handleProviderChange('right_brain', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-right-provider">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_PROVIDERS.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Model</Label>
                      <Select
                        value={agent.ai_config?.right_brain_model || 'claude-sonnet-4-5-20250929'}
                        onValueChange={(val) => handleConfigChange('right_brain_model', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-right-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS[agent.ai_config?.right_brain_provider || 'anthropic']?.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Brain Stem */}
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-emerald-600" />
                      Brain Stem
                    </CardTitle>
                    <CardDescription className="text-xs">Synthesis & Output</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <Label className="text-xs">Provider</Label>
                      <Select
                        value={agent.ai_config?.brain_stem_provider || 'gemini'}
                        onValueChange={(val) => handleProviderChange('brain_stem', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-stem-provider">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AI_PROVIDERS.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Model</Label>
                      <Select
                        value={agent.ai_config?.brain_stem_model || 'gemini-2.5-flash'}
                        onValueChange={(val) => handleConfigChange('brain_stem_model', val)}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="edit-agent-stem-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS[agent.ai_config?.brain_stem_provider || 'gemini']?.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Temperature & Max Tokens */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Temperature ({agent.ai_config?.temperature || 0.7})</Label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={agent.ai_config?.temperature || 0.7}
                    onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                    className="w-full mt-2"
                    data-testid="edit-agent-temperature"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lower = more focused, Higher = more creative
                  </p>
                </div>
                <div>
                  <Label>Max Tokens</Label>
                  <Input
                    type="number"
                    value={agent.ai_config?.max_tokens || 2000}
                    onChange={(e) => handleConfigChange('max_tokens', parseInt(e.target.value))}
                    data-testid="edit-agent-max-tokens"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Maximum response length</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="edit-agent-cancel">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="edit-agent-save">
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAgentDialog;
